import { useEffect, useLayoutEffect, useRef } from 'react'

import styles from './carousel.module.scss'

interface CarouselProps {
    activeIndex: number
    className: string

    items: React.ReactNode[]
    setActiveIndex: (index: number) => void
}

const getIndexFromScroll = (track: HTMLDivElement, slideCount: number) => {
    const slideWidth = track.clientWidth

    if (slideWidth <= 0 || slideCount === 0) {
        return 0
    }

    return Math.min(slideCount - 1, Math.max(0, Math.round(track.scrollLeft / slideWidth)))
}

export const Carousel = ({
    className,

    items,
    activeIndex,
    setActiveIndex,
}: CarouselProps) => {
    const viewportRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const slideRef = useRef<(HTMLDivElement | null)[]>([])

    useLayoutEffect(() => {
        const viewport = viewportRef.current
        const slide = slideRef.current[activeIndex]

        if (!viewport || !slide) {
            return
        }

        const updateHeight = () => {
            viewport.style.height = `${slide.getBoundingClientRect().height}px`
        }

        updateHeight()

        const resizeObserver = new ResizeObserver(updateHeight)

        resizeObserver.observe(slide)

        return () => resizeObserver.disconnect()
    }, [activeIndex, items.length])

    useEffect(() => {
        const track = trackRef.current

        if (!track || items.length < 2) {
            return
        }

        const syncIndexFromScroll = () => {
            const index = getIndexFromScroll(track, items.length)

            setActiveIndex(index)
        }

        track.addEventListener('scrollend', syncIndexFromScroll)

        let scrollEndTimer: ReturnType<typeof setTimeout> | undefined

        const onScroll = () => {
            if (scrollEndTimer) {
                clearTimeout(scrollEndTimer)
            }

            scrollEndTimer = setTimeout(syncIndexFromScroll, 80)
        }

        track.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            track.removeEventListener('scrollend', syncIndexFromScroll)
            track.removeEventListener('scroll', onScroll)

            if (scrollEndTimer) {
                clearTimeout(scrollEndTimer)
            }
        }
    }, [items.length, setActiveIndex])

    const goTo = (index: number) => {
        const track = trackRef.current

        if (!track) {
            return
        }

        track.scrollTo({ behavior: 'auto', left: index * track.clientWidth })
        setActiveIndex(index)
    }

    if (items.length === 0) {
        return null
    }

    return (
        <div className={`${styles['carousel__root']} ${className}`}>
            <div ref={viewportRef} className={styles['carousel__viewport']}>
                <div ref={trackRef} className={styles['carousel__track']}>
                    {items.map((children, index) => (
                        <div
                            // eslint-disable-next-line @eslint-react/no-array-index-key
                            key={index}
                            ref={(node) => {
                                slideRef.current[index] = node
                            }}
                            className={styles['carousel__slide']}
                        >
                            {children}
                        </div>
                    ))}
                </div>
            </div>

            {items.length > 1 ?
                <div className={styles['carousel__dots']}>
                    {items.map((_, index) => (
                        <button
                            // eslint-disable-next-line @eslint-react/no-array-index-key
                            key={index}
                            aria-label={`Слайд ${index + 1}`}
                            type='button'
                            className={`${styles['carousel__dot']} ${
                                index === activeIndex ? styles['carousel__dot--active'] : ''
                            }`}
                            onClick={() => goTo(index)}
                        />
                    ))}
                </div>
            :   null}
        </div>
    )
}
